import React, { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Button, Image, Input, Select, Space, message } from "antd";
import { useTranslation } from "react-i18next";
import { getSkuPhotoDownloadUrl, uploadSkuPhoto } from "../../api/Services";

const FILE_REFERENCE_PREFIX = "file:";

type PhotoSource = "url" | "upload";

type SkuPhotoInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

const getStoredFileId = (value?: string) =>
  value?.startsWith(FILE_REFERENCE_PREFIX)
    ? value.slice(FILE_REFERENCE_PREFIX.length)
    : undefined;

const SkuPhotoInput = ({ value, onChange, disabled }: SkuPhotoInputProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSource, setSelectedSource] = useState<PhotoSource>("url");
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [uploading, setUploading] = useState(false);
  const fileId = getStoredFileId(value);
  const source: PhotoSource = fileId
    ? "upload"
    : value
      ? "url"
      : selectedSource;

  useEffect(() => {
    if (value === undefined) setSelectedSource("url");
  }, [value]);

  useEffect(() => {
    let current = true;
    setPreviewUrl(undefined);

    if (fileId) {
      getSkuPhotoDownloadUrl(fileId)
        .then((url) => {
          if (current) setPreviewUrl(url);
        })
        .catch(() => {
          if (current) setPreviewUrl(undefined);
        });
    } else if (value?.trim()) {
      setPreviewUrl(value.trim());
    }

    return () => {
      current = false;
    };
  }, [fileId, value]);

  const changeSource = (nextSource: PhotoSource) => {
    setSelectedSource(nextSource);
    onChange?.("");
  };

  const handleFileSelection = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error(t("SkuPhoto-ChooseImage"));
      event.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const uploadedFileId = await uploadSkuPhoto(file);
      setSelectedSource("upload");
      onChange?.(`${FILE_REFERENCE_PREFIX}${uploadedFileId}`);
    } catch {
      message.error(t("SkuPhoto-UploadFailed"));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Select
        value={source}
        onChange={changeSource}
        disabled={disabled}
        options={[
          { value: "url", label: t("SkuPhoto-Address") },
          { value: "upload", label: t("SkuPhoto-Upload") },
        ]}
      />
      {source === "url" ? (
        <Input
          value={fileId ? "" : value ?? ""}
          maxLength={255}
          disabled={disabled}
          placeholder={t("SkuPhoto-AddressPlaceholder")}
          onChange={(event) => onChange?.(event.target.value)}
        />
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileSelection}
          />
          <Space wrap>
            <Button
              htmlType="button"
              disabled={disabled}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {fileId ? t("SkuPhoto-Change") : t("SkuPhoto-Select")}
            </Button>
            {value && (
              <Button
                htmlType="button"
                type="link"
                disabled={disabled}
                onClick={() => onChange?.("")}
              >
                {t("SkuPhoto-Remove")}
              </Button>
            )}
          </Space>
        </>
      )}
      {previewUrl && (
        <Image
          src={previewUrl}
          alt={t("Common-Photo")}
          width={160}
          style={{ maxHeight: 120, objectFit: "contain" }}
        />
      )}
    </Space>
  );
};

export default SkuPhotoInput;
